import posthog from 'posthog-js';

import {
  bindPosthog,
  dropNoisyExceptions,
  enrichExceptionMessage,
  LLM_REFERRER_HOSTS as LLM_REFERRER_HOSTS_CANONICAL,
  LLM_SOURCES as LLM_SOURCES_CANONICAL,
} from '@repo/analytics';

import { env } from '@env';
import { captureFirstTouchAcquisition } from '@/lib/analytics/auth-telemetry';
import { scrubAuthParamsBeforeSend } from '@/lib/analytics/scrub-auth-params';
import { viewportBucket } from '@/lib/analytics/viewport';

posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY!, {
  api_host: '/ingest',
  ui_host: 'https://us.posthog.com',
  defaults: '2025-05-24',
  capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
  // debug: process.env.NODE_ENV === 'development',
  debug: false,
  // AUTH-3 (2026-07-05): redact OAuth token/user query-param values from
  // every outgoing event (incl. $set/$set_once payloads) BEFORE they leave
  // the browser. The redirect sign-in callback URL carries
  // access_token/refresh_token/user JSON; without this, $current_url and the
  // first-touch person props stored live credentials + PII. See
  // src/lib/analytics/scrub-auth-params.ts for the full rationale.
  // Three filters, in this order (CRE-983 P0.4 inserts the enricher in the
  // middle). Dropping FIRST means an event we are about to discard is never
  // enriched or scrubbed. Enriching SECOND promotes the $exception cause to a
  // bounded, queryable error_message/error_type. Scrubbing LAST redacts known
  // credential shapes the copied message might carry — sensitive URL params AND
  // free-text JWT/Bearer tokens + emails (CRE-983 hardening) — so a token or
  // email embedded in an error string is redacted before the event leaves the
  // browser. Best-effort, not a guarantee against all free-form PII. The three
  // have distinct jobs: drop removes events, enrich only adds bounded props,
  // scrub only redacts — none ever throws into the capture path (CRE-641).
  before_send: [
    dropNoisyExceptions,
    enrichExceptionMessage,
    scrubAuthParamsBeforeSend,
  ],
  // Privacy: mask all input values in session recordings. Password inputs
  // are masked by default; this extends to every <input>, <textarea>, and
  // <select> so email fields, search boxes, and any future payment inputs
  // are never captured in replays. Page text remains visible so we can
  // still see which analysis content the user is reading.
  session_recording: {
    maskAllInputs: true,
    // Replay budget (5K web recordings/mo free pool): sampling + the sub-3s
    // bounce-drop (minimumDurationMilliseconds) + LLM-cohort 100% capture are
    // configured in the PostHog UI (Settings -> Recordings -> Recording
    // conditions), matching session-replay-llm-cohort-setup.md — kept out of
    // code so the cohort rules can change without a deploy. Huge headroom at
    // current volume; revisit sampleRate here if replay volume nears the cap.
  },
});

// Bind the posthog-js singleton into the typed `@repo/analytics` wrapper so
// `track(event, props)` actually captures on the website. Without this the
// typed wrapper silently no-ops (posthogClient stays null) and every event has
// to fall back to raw `posthog.capture`, which trips the analytics-wrapper-gate.
// This is the one allowlisted place to do it (see analytics-wrapper-gate.yml).
bindPosthog(posthog);

// shell_variant is a rollout super-property (doctrine §"redesign-specific"):
// register it ONCE so every event is splittable v1-vs-v2 without per-call
// plumbing. home-v2 is the only shell now, so this is constant 'v2'; kept as a
// super-property to preserve historical v1-vs-v2 comparability and the doctrine
// shape. `surface: 'website'` is already registered below.
posthog.register({ shell_variant: 'v2' });

// Register landing-time properties on every event in this browser session.
// This file runs once per browser session at boot, so reading the URL here
// captures the user's *original* entry context, not whatever page they
// happen to be on later via SPA navigation.
//
// Why this matters:
// - `surface` lets analytics cleanly segment skim.plus (public website)
//   from extension.skim.plus (the embedded iframe webapp). Without it,
//   every funnel was contaminated by ~76% iframe traffic.
// - `landed_on_keypoint` + `kp_id` are the only way to measure the viral
//   keypoint-link share loop ("Aha #2"). Every event with
//   landed_on_keypoint=true on a non-skim.plus referrer is a successful
//   share-recipient landing.
// Domains that AI assistants append as ?utm_source=<domain> when surfacing
// citation links to web pages. Used to detect "an LLM sent us a visitor"
// and fire the canonical `llm_citation_landed` event for funnel/cohort
// analysis. Add new sources here as we observe them in the wild.
//
// Item 49 (single-sourcing): the host lists themselves are now the CANONICAL
// exported constants in `@repo/analytics` (packages/analytics/src/llm-sources.ts)
// — imported above and wrapped in Sets here for O(1) `.has()` lookups. The
// coverage rationale + the referrer-subset rationale live at the canonical
// source. apps/landing/instrumentation-client.ts imports the same constants,
// so the two clients can no longer drift.
const LLM_SOURCES = new Set<string>(LLM_SOURCES_CANONICAL);
const LLM_REFERRER_HOSTS = new Set<string>(LLM_REFERRER_HOSTS_CANONICAL);

if (typeof window !== 'undefined') {
  // Entry-touch acquisition capture (Wave-4 item 37). Persists the landing
  // referrer + utm_source/medium/campaign into sessionStorage BEFORE any
  // user interaction, so the OAuth-init URL builder (collectAcquisitionParams
  // in auth-telemetry.ts) still has the landing UTMs after SPA navigation
  // strips them from window.location.search. Internal navigations and bare
  // direct re-entries never overwrite a captured external/tagged entry.
  // Fail-open by construction - never throws into analytics boot.
  captureFirstTouchAcquisition();

  const params = new URLSearchParams(window.location.search);
  const kp = params.get('kp');

  // Capture every UTM parameter as a sticky property on every event in this
  // browser session. PostHog's auto-capture writes UTM params to
  // properties.$utm_* (with $ prefix) but our daily insights bot reads
  // properties.utm_* (no prefix). Registering explicitly keeps both naming
  // conventions populated so neither code path silently misses data.
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');
  const utmContent = params.get('utm_content');
  const utmTerm = params.get('utm_term');

  const utmLlmSource =
    utmSource && LLM_SOURCES.has(utmSource.toLowerCase())
      ? utmSource.toLowerCase()
      : null;

  // Referrer fallback: when utm_source misses, match document.referrer's
  // hostname against the conservative chat-host set. utm wins when both are
  // present. Wrapped so a malformed referrer can never break analytics boot.
  let referrerLlmSource: string | null = null;
  if (!utmLlmSource) {
    try {
      const rawReferrer =
        typeof document !== 'undefined' ? document.referrer : '';
      if (rawReferrer) {
        const referrerHost = new URL(rawReferrer).hostname
          .toLowerCase()
          .replace(/^(www|m)\./, '');
        if (LLM_REFERRER_HOSTS.has(referrerHost)) {
          referrerLlmSource = referrerHost;
        }
      }
    } catch {
      // A malformed referrer must never break analytics boot.
    }
  }

  const llmSource = utmLlmSource ?? referrerLlmSource;

  posthog.register({
    surface: 'website',
    viewport: viewportBucket(window.innerWidth),
    landed_on_keypoint: !!kp,
    kp_id: kp ?? null,
    utm_source: utmSource ?? null,
    utm_medium: utmMedium ?? null,
    utm_campaign: utmCampaign ?? null,
    utm_content: utmContent ?? null,
    utm_term: utmTerm ?? null,
    llm_source: llmSource,
  });

  // ── ext_install_id cross-surface join key (CRE-983 P0.5 / h2d A14) ────────
  // The browser extension mints a stable install id (its posthog distinct_id,
  // persisted in chrome.storage.local under `posthog_distinct_id`). Registering
  // it as a SUPER-PROPERTY here lets cross-surface events from the same install
  // be de-duped/joined (five of the busiest people are counted twice today
  // because the panel id and the web id share no join key).
  //
  // NEVER `identify()` on it — it is a DEVICE join key, not a person id; a
  // merge on it would poison person-level counts (h2d A14). Shape-validated
  // (uuid-ish) and registered at most once; both discovery paths are fail-open.
  //
  // Transport: the WEBAPP RECEIVE side is implemented in full below. The
  // EXTENSION EMIT side is a separate-repo PR (see the operational notes /
  // handoff): the extension must append `?ext_install_id=<distinct_id>` to any
  // skim.plus URL it opens, or postMessage `{ type: 'extension-context',
  // ext_install_id }` if it embeds skim.plus. Until then this is inert (no-op),
  // which is why it is safe to ship the receive side now.
  const EXT_INSTALL_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
  let extInstallIdRegistered = false;
  const registerExtInstallId = (raw: unknown): void => {
    if (extInstallIdRegistered) return;
    if (typeof raw !== 'string' || !EXT_INSTALL_ID_RE.test(raw)) return;
    extInstallIdRegistered = true;
    try {
      posthog.register({ ext_install_id: raw });
    } catch {
      // A bad register must never break analytics boot.
    }
  };

  // Path 1 (primary, mirrors the apps/landing iframe transport): the query
  // param the extension appends to a skim.plus link it opens. SPA navigation
  // strips it from the URL, but `register` is sticky for the whole session.
  registerExtInstallId(params.get('ext_install_id'));

  // Path 2 (embedded case): a postMessage from an embedding frame. Guarded to
  // the parent window, a strict message shape, AND an explicit origin allowlist
  // (CRE-983 hardening). `event.source === window.parent` alone is NOT enough:
  // any third-party page that iframes skim.plus becomes window.parent, so
  // without an origin check an arbitrary embedder could postMessage a poisoned
  // ext_install_id into this sticky super-property (attacker-chosen device join
  // key -> corrupted cross-surface de-dup). skim.plus (this app) is a
  // standalone TOP-LEVEL site; the extension embeds apps/landing
  // (extension.skim.plus), NOT this app, so there is no legitimate cross-origin
  // sender today and same-origin is the correct allowlist. When the extension
  // emit side ships a real cross-origin sender, add its exact origin (e.g. the
  // chrome-extension:// origin) to EXT_CONTEXT_ORIGINS — never widen to '*'.
  const EXT_CONTEXT_ORIGINS = new Set<string>([window.location.origin]);
  try {
    if (window.parent && window.parent !== window) {
      window.addEventListener('message', (event: MessageEvent) => {
        if (event.source !== window.parent) return;
        if (!EXT_CONTEXT_ORIGINS.has(event.origin)) return;
        const data = event.data as {
          type?: unknown;
          ext_install_id?: unknown;
        };
        if (data && data.type === 'extension-context') {
          registerExtInstallId(data.ext_install_id);
        }
      });
    }
  } catch {
    // Origin/shape guards must never break analytics boot.
  }

  // Keep the `viewport` super-property current as the user rotates / resizes.
  // Re-registering is local-only (no event fired), debounced to avoid churn,
  // so every subsequent event is splittable by the live breakpoint.
  let viewportResizeTimer: ReturnType<typeof setTimeout> | null = null;
  let lastViewport = viewportBucket(window.innerWidth);
  window.addEventListener(
    'resize',
    () => {
      if (viewportResizeTimer) clearTimeout(viewportResizeTimer);
      viewportResizeTimer = setTimeout(() => {
        const next = viewportBucket(window.innerWidth);
        if (next !== lastViewport) {
          lastViewport = next;
          posthog.register({ viewport: next });
        }
      }, 250);
    },
    { passive: true },
  );

  // Universal first-touch attribution — sticky person-properties set
  // ONCE per anonymous visitor and preserved across `posthog.identify()`
  // at signup time. Enables cohort splits like:
  //   • "users who landed on a kp= variant" vs "users who landed on /home"
  //   • "users whose first referrer was google.com" vs LLM-referred
  //   • "users who first landed on /videos/youtube/* vs /articles/*"
  //
  // These run for ALL visitors (not just LLM-referred) — the LLM-specific
  // person-props below are a layered subset for the 7× cohort. Without
  // first-touch attribution on the non-LLM majority, we couldn't compare
  // conversion lift across landing types.
  // Bare referring host (or `$direct` when there is no referrer) alongside
  // the full-string `initial_referrer` below. Downstream channel derivation +
  // the acq-on-OAuth-URL join (CRE-854) read the bare host, not the full URL.
  // `$direct` is a first-class value (not null): an empty referrer is real
  // signal (~85% of Skim signups are direct), and the backend allowlists it.
  // set_once so a later visit from a different referrer never overwrites the
  // recorded first-touch. Wrapped so a bad referrer can never break boot.
  let initialReferringDomain: string | null = null;
  try {
    if (typeof document !== 'undefined') {
      const rawReferrer = document.referrer;
      if (!rawReferrer) {
        initialReferringDomain = '$direct';
      } else {
        const host = new URL(rawReferrer).hostname;
        // Reduce mobile / link-shim subdomains to their canonical host so the
        // first-touch super-prop agrees with the OAuth-URL acq_referrer, which
        // applies the same reduction in auth-telemetry's normalizeHost (AW-2).
        // Anchored to exact prefix tokens + a dot so we never over-strip a
        // legitimate second-level host (e.g. medium.com, line.me).
        initialReferringDomain = host
          ? host.toLowerCase().replace(/^(www|m|mobile|l|lm|touch)\./, '')
          : '$direct';
      }
    }
  } catch {
    initialReferringDomain = null;
  }

  posthog.people.set_once({
    initial_landing_path: window.location.pathname,
    initial_landing_url: window.location.href,
    initial_referrer:
      typeof document !== 'undefined' ? document.referrer || null : null,
    initial_referring_domain: initialReferringDomain,
    initial_landed_at: new Date().toISOString(),
    initial_landed_on_keypoint: !!kp,
    initial_kp_id: kp ?? null,
    initial_utm_source: utmSource ?? null,
    initial_utm_medium: utmMedium ?? null,
    initial_utm_campaign: utmCampaign ?? null,
    initial_surface: 'website',
  });

  // Fire a one-time-per-session event when an LLM-referred landing is
  // detected. This is the canonical "LLM cited us" signal — funnels,
  // cohort analysis, and the daily insights bot key off this single event.
  if (llmSource) {
    posthog.capture('llm_citation_landed', {
      llm_source: llmSource,
      // 'utm' vs 'referrer' keeps the two detection legs separable in
      // analysis (utm-tagged citations vs bare-referrer chat traffic).
      llm_source_match: utmLlmSource ? 'utm' : 'referrer',
      landing_url: window.location.href,
      landing_path: window.location.pathname,
      utm_campaign: utmCampaign ?? null,
      kp_id: kp ?? null,
      surface: 'website',
    });

    // Sticky person-properties so post-signup cohort analysis works
    // ("retention of LLM-referred users vs SEO-referred"). Set survives
    // posthog.identify() at signup time. set_once preserves first-touch
    // attribution if the same user lands again from a different LLM later.
    //
    // first_traffic_source is a FIRST-TOUCH prop, so it must be set_once (not
    // set). With plain set, a returning LLM-referred visitor overwrote their
    // original first-touch source every landing (CRE-851 clobber fix). set_once
    // writes it only on the first anonymous landing and preserves it through
    // the anon->identified merge at signup.
    posthog.people.set_once({
      first_traffic_source: `llm:${llmSource}`,
    });
    posthog.people.set_once({
      ever_came_from_llm: true,
      first_llm_source: llmSource,
      first_llm_landing_at: new Date().toISOString(),
    });
  }
}
